import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useState } from "react";
import { CalendarCheck, Star, Rocket, ArrowsDownUp, Play, Key, Exam, ChatCircleDots, Medal, Target, Trophy, UsersThree, Flask, Sparkle, CheckCircle, type Icon } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { useAchievements, useSaveAchievements } from "@/hooks/useEngagement";
import { ACHIEVEMENT_ICON, TIER_COLOR, achievementAmount, nextNeed, type Achievement } from "@/lib/engagement.core";
import { LEARN_ARTICLES } from "@/lib/learn.core";
import { QuizBadge } from "@/components/engagement/Learn";
import { useQuizzes } from "@/hooks/useEngagement";

/****************
 * ACHIEVEMENTS ON THE WEB. 2026-10-24.
 *
 * Guillaume: Bronze/Silver/Gold badges, an Achievements section on the
 * profile, the top 3 chosen to show beside the name in Community, and
 * milestones driven by the user's own objectives (one achievement is the goal).
 * CopyFi has no profile page, so this is the profile's achievements, at
 * /learn/achievements, linked from the Learn hub and Community.
 ****************/

const ICONS: Record<string, Icon> = { CalendarCheck, Star, Rocket, ArrowsDownUp, Play, Key, Exam, ChatCircleDots, Trophy, Medal, UsersThree, Flask, Sparkle };

export function AchievementIcon({ a, size }: { a: Achievement; size: number }) {
  const I = ICONS[ACHIEVEMENT_ICON[a.key]] || Medal;
  return <I size={size} weight={a.tier ? "fill" : "regular"} style={a.tier ? { color: TIER_COLOR[a.tier] } : undefined}
    className={a.tier ? "shrink-0" : "shrink-0 text-muted-foreground"} />;
}

/** The chosen badges beside a name in Community. */
export function BadgeRow({ badges }: { badges?: Achievement[] }) {
  const { t } = useTranslation("learn");
  if (!badges?.length) return null;
  return (
    <span className="inline-flex items-center gap-0.5 ms-1.5 align-middle">
      {badges.map((b) => (
        <span key={b.key} title={`${t(`ach.k.${b.key}.title`)} · ${t(`ach.tier.${b.tier}`)}`}><AchievementIcon a={b} size={14} /></span>
      ))}
    </span>
  );
}

export function AchievementsPage({ embedded = false }: { embedded?: boolean } = {}) {
  const { t } = useTranslation("learn");
  const q = useAchievements();
  const save = useSaveAchievements();
  const quizzes = useQuizzes();
  const perfect = new Set((quizzes.data?.quizzes ?? []).filter((x) => x.perfect_at).map((x) => x.quiz));
  const showcase = q.data?.showcase ?? [];
  // Up to five followed (Guillaume 10-27, round 8 #214; three since 10-25).
  const goals = q.data?.goals ?? (q.data?.goal ? [q.data.goal] : []);
  const isGoal = (k: string) => goals.includes(k);
  const toggleGoal = (k: string) => {
    if (isGoal(k)) return put({ goals: goals.filter((g) => g !== k) });
    if (goals.length >= 5) return toast.error(t("ach.goalsFull"));
    put({ goals: [...goals, k] });
  };
  const put = (body: { showcase?: string[]; goal?: string; goals?: string[] }) =>
    save.mutate(body, { onError: (e: any) => toast.error(e?.message || t("ach.saveFailed")) });
  // Clicking a badge opens its own page (Guillaume 10-25).
  const [opened, setOpened] = useState<Achievement | null>(null);
  const toggleShow = (key: string) => {
    if (showcase.includes(key)) return put({ showcase: showcase.filter((k) => k !== key) });
    if (showcase.length >= 3) return toast.error(t("ach.showcaseFull"));
    put({ showcase: [...showcase, key] });
  };
  return (
    <div className={embedded ? "w-full" : "p-6 lg:p-10 max-w-5xl mx-auto w-full"} data-testid="learn-achievements">
      <div className={embedded ? "mb-4" : "mb-8"}>
        <h1 className="text-2xl font-semibold text-foreground">{t("ach.title")}</h1>
        <p className="text-sm text-muted-foreground mt-1">{t("ach.subtitle")}</p>
        <p className="text-xs text-muted-foreground mt-2">{t("ach.showcaseHelp")} {t("ach.goalHelp")}</p>
      </div>
      {q.isError ? <p className="text-sm text-danger">{t("ach.loadFailed")}</p> : (
        <div className="grid gap-3 md:grid-cols-2" data-tour="ach-goals">
          {(q.data?.achievements ?? []).map((a) => {
            const need = nextNeed(a);
            const shown = showcase.includes(a.key);
            return (
              <div key={a.key} className={`glass-card rounded-xl p-4 space-y-3 ${isGoal(a.key) ? "border-primary/60" : ""}`}>
                <button type="button" onClick={() => setOpened(a)} className="flex items-start gap-3 w-full text-start hover:opacity-90">
                  <AchievementIcon a={a} size={28} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold">{t(`ach.k.${a.key}.title`)}
                      <span className="ms-2 text-xs font-medium" style={a.tier ? { color: TIER_COLOR[a.tier] } : undefined}>{t(`ach.tier.${a.tier}`)}</span>
                    </p>
                    <p className="text-xs text-muted-foreground">{t(`ach.k.${a.key}.body`)}</p>
                  </div>
                  {isGoal(a.key) && <Target size={18} className="text-primary shrink-0" aria-label={t("ach.goalOn")} />}
                </button>
                {need !== null ? (
                  <div className="space-y-1">
                    <Progress value={Math.min(100, (100 * (a.have ?? 0)) / need)} className="h-1.5" />
                    <p className="text-[11px] text-muted-foreground">{t("ach.next", { have: achievementAmount(a, a.have ?? 0), need: achievementAmount(a, need), tier: t(`ach.tier.${a.tier + 1}`) })}</p>
                  </div>
                ) : <p className="text-[11px]" style={{ color: TIER_COLOR[3] }}>{t("ach.max")}</p>}
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant={shown ? "default" : "outline"} disabled={save.isPending || (!a.tier && !shown)} onClick={() => toggleShow(a.key)}>
                    {/* The action the button takes (Guillaume 11-04). */}{shown ? t("ach.showStop") : t("ach.showSet")}
                  </Button>
                  {/* No goal once gold: there is nothing higher to reach (Guillaume 10-25). */}
                  {(need !== null || isGoal(a.key)) && (
                  <Button size="sm" variant={isGoal(a.key) ? "default" : "ghost"} disabled={save.isPending} onClick={() => toggleGoal(a.key)}>
                    {isGoal(a.key) ? t("ach.goalRemove") : t("ach.goalSet")}
                  </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      <Dialog open={!!opened} onOpenChange={(o) => !o && setOpened(null)}>
        <DialogContent className="max-w-md">
          {opened && (
            <>
              <DialogHeader className="items-center text-center">
                <AchievementIcon a={opened} size={64} />
                <DialogTitle className="mt-2">{t(`ach.k.${opened.key}.title`)}</DialogTitle>
                <DialogDescription>{t(`ach.k.${opened.key}.body`)}</DialogDescription>
              </DialogHeader>
              <div className="space-y-2">
                {(opened.tiers ?? []).map((need, i) => {
                  const got = opened.tier > i;
                  return (
                    <div key={i} className="flex items-center gap-3 rounded-lg border border-border/60 px-3 py-2">
                      <Medal size={22} weight={got ? "fill" : "regular"} style={{ color: got ? TIER_COLOR[i + 1] : undefined }} className={got ? "" : "text-muted-foreground"} />
                      <span className="flex-1 text-sm font-medium">{t(`ach.tier.${i + 1}`)}</span>
                      <span className="text-sm tabular-nums text-muted-foreground">{achievementAmount(opened, need)}</span>
                      {got && <CheckCircle size={18} weight="fill" className="text-success" />}
                    </div>
                  );
                })}
                <p className="text-xs text-muted-foreground text-center pt-1">{t("ach.youHave", { have: achievementAmount(opened, opened.have ?? 0) })}</p>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
      <h2 className="text-lg font-semibold mt-10 mb-3">{t("ach.quizBadges")}</h2>
      <div className="flex flex-wrap gap-3">
        {LEARN_ARTICLES.map((a) => (
          <span key={a.id} title={t(`art.${a.id}.title`)} className="glass-card rounded-xl p-2.5"><QuizBadge id={a.id} size={24} earned={perfect.has(a.id)} /></span>
        ))}
      </div>
    </div>
  );
}
